
import React, { useState } from 'react';
import { Ticket, AppSettings, Employee } from '../types';
import { Button } from './Button';
import { PrintableCheckInAgreement } from './PrintableCheckInAgreement';

interface CheckInProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: Ticket;
  appSettings: AppSettings;
  onAddInternalTicketNote: (ticketId: string, noteText: string, employeeName?: string) => void;
  onOpenPrintModal: (title: string, content: React.ReactNode, contentClassName?: string) => void;
  currentUser: Employee | null;
}

const DocumentTextIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
);
const PrinterIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
  </svg>
);


export const CheckInProcessModal: React.FC<CheckInProcessModalProps> = ({
  isOpen,
  onClose,
  ticket,
  appSettings,
  onAddInternalTicketNote,
  onOpenPrintModal,
  currentUser
}) => {
  const [noteText, setNoteText] = useState('');

  if (!isOpen) return null;

  const handleAddNote = () => {
    if (noteText.trim()) {
      onAddInternalTicketNote(ticket.id, `Intake Note: ${noteText.trim()}`, currentUser?.name || "Technician");
      setNoteText(''); 
    }
  };

  const handlePrintIntake = () => {
    onOpenPrintModal(
      `Intake Agreement - ${ticket.id}`,
      <PrintableCheckInAgreement ticket={ticket} appSettings={appSettings} />,
      'label-content-area'
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-[110]" // Higher z-index than TicketItem
      aria-labelledby="checkin-process-modal-title"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 border-b pb-3">
          <h2 id="checkin-process-modal-title" className="text-xl font-semibold text-slate-800 flex items-center">
            <DocumentTextIcon /> Process Check-in: {ticket.id}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
            aria-label="Close modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <h3 className="text-md font-semibold text-slate-700">Ticket Details:</h3>
            <p className="text-sm text-slate-600"><strong>Customer:</strong> {ticket.customer}</p>
            <p className="text-sm text-slate-600"><strong>Device:</strong> {ticket.deviceModel || 'N/A'}</p>
            <p className="text-sm text-slate-600"><strong>Issue:</strong> {ticket.subject}</p>
          </div>

          <div>
            <label htmlFor="checkInNote" className="block text-sm font-medium text-slate-700 mb-1">
              Technician Intake Note:
            </label>
            <textarea
              id="checkInNote"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={3}
              placeholder="e.g., Verified issue, device condition, customer requests..."
              className="w-full p-2 border border-slate-300 rounded-md text-sm focus:ring-1 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)]"
            />
            <Button
              onClick={handleAddNote}
              variant="ghost"
              className="text-xs mt-1 py-1 px-2 !text-[var(--color-primary)] hover:!bg-[var(--color-primary)] hover:!bg-opacity-10"
              disabled={!noteText.trim()}
            >
              Add Note to Ticket
            </Button>
          </div>
          
          <div>
            <Button
              onClick={handlePrintIntake}
              variant="secondary"
              className="w-full flex items-center justify-center"
            >
              <PrinterIcon /> Print Intake Agreement
            </Button>
          </div>

        </div>

        <div className="border-t pt-4 mt-6 flex justify-end">
          <Button type="button" variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};